import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom'; // 💉 CHIRURGIE: Portal für den Ausbruch aus der Sidebar
import { auth, db } from '../config/firebase';
import { doc, getDoc, getDocs, collection, setDoc } from 'firebase/firestore'; 
import { AnimatePresence, motion } from 'framer-motion';
import { shouldBypassAutoBackup } from '../utils/backupBypass';
import {
    type BackupFolderSlot,
    clearSavedBackupFolder,
    getSavedBackupFolderName,
    pickBackupDirectory,
    saveBackupJson,
    supportsBackupFolderPicker,
} from '../utils/backupFolder';

const DownloadIcon = (props: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 20} height={props.size || 20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={props.className}>
        <path d="M21 15v4a2 2 0 0 1-2-2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);

const UploadIcon = (props: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 20} height={props.size || 20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={props.className}>
        <path d="M21 15v4a2 2 0 0 1-2-2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
);

const ShieldAlertIcon = (props: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 24} height={props.size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={props.className}>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
);

interface GlobalBackupManagerProps {
    showNotification: (msg: string, type: 'success' | 'error') => void;
    variant?: 'button' | 'guardian' | 'both'; 
}

const SUPERADMIN_EMAIL = 'erfolg@friseur-coaching.de';

const isSuperAdminUser = () => auth.currentUser?.email?.toLowerCase() === SUPERADMIN_EMAIL;

const GlobalBackupManager: React.FC<GlobalBackupManagerProps> = ({ showNotification, variant = 'both' }) => {
    const [isDownloadingMaster, setIsDownloadingMaster] = useState(false);
    const [showGuardianModal, setShowGuardianModal] = useState(false);

    // States für das zweite, unabhängige 72h-GlobalSettings-Backup
    const [isDownloadingGlobalSettings, setIsDownloadingGlobalSettings] = useState(false);
    const [showGlobalSettingsGuardianModal, setShowGlobalSettingsGuardianModal] = useState(false);

    // States & Refs für den Global Restore
    const [isRestoring, setIsRestoring] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [restoreData, setRestoreData] = useState<any | null>(null);
    const [restoreCode, setRestoreCode] = useState('');
    const [restoreError, setRestoreError] = useState(false);
    /** null = Modus wählen, 'full' = gesamtes System, 'single' = eine Akte */
    const [restoreMode, setRestoreMode] = useState<'full' | 'single' | null>(null);
    const [singleAkteId, setSingleAkteId] = useState('');
    const [singleAkteSearch, setSingleAkteSearch] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [masterFolderName, setMasterFolderName] = useState(() => getSavedBackupFolderName('master'));
    const [globalFolderName, setGlobalFolderName] = useState(() => getSavedBackupFolderName('global'));
    const canPickFolder = supportsBackupFolderPicker();

    const folderNameFor = (slot: BackupFolderSlot) =>
        slot === 'master' ? masterFolderName : globalFolderName;

    const refreshBackupFolderName = (slot: BackupFolderSlot) => {
        const name = getSavedBackupFolderName(slot);
        if (slot === 'master') setMasterFolderName(name);
        else setGlobalFolderName(name);
    };

    const handlePickBackupFolder = async (slot: BackupFolderSlot) => {
        try {
            const handle = await pickBackupDirectory(slot);
            if (handle) {
                refreshBackupFolderName(slot);
                const label = slot === 'master' ? 'Master-Mandanten' : 'Global';
                showNotification(`${label}-Ordner gesetzt: ${handle.name}`, 'success');
            }
        } catch (err: any) {
            console.error('Ordnerwahl fehlgeschlagen:', err);
            showNotification('Ordner konnte nicht gesetzt werden.', 'error');
        }
    };

    const handleClearBackupFolder = async (slot: BackupFolderSlot) => {
        await clearSavedBackupFolder(slot);
        refreshBackupFolderName(slot);
        showNotification('Fester Ordner entfernt — Downloads gehen wieder in den Standard-Ordner.', 'success');
    };

    const FolderHint = ({
        slot,
        accent = 'gold',
    }: {
        slot: BackupFolderSlot;
        accent?: 'gold' | 'cyan';
    }) => {
        const border = accent === 'cyan' ? 'border-cyan-500/30' : 'border-[#E6C299]/25';
        const label = accent === 'cyan' ? 'text-cyan-300' : 'text-[#E6C299]';
        const name = folderNameFor(slot);
        const slotLabel = slot === 'master' ? 'Master-Mandanten-Backup' : 'Global-Backup';
        return (
            <div className={`w-full mb-5 p-3 rounded-xl bg-black/40 border ${border} text-left`}>
                <div className={`text-[10px] uppercase font-black tracking-widest ${label} mb-1`}>
                    Speicherort · {slotLabel}
                </div>
                {canPickFolder ? (
                    <>
                        <p className="text-xs text-slate-400 mb-2 leading-relaxed">
                            {name
                                ? <>Aktuell: <span className="text-white font-bold">{name}</span></>
                                : 'Noch kein eigener Ordner gesetzt — Datei landet sonst im Downloads-Ordner.'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => { void handlePickBackupFolder(slot); }}
                                className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest bg-slate-800 hover:bg-slate-700 text-white border border-slate-600"
                            >
                                {name ? 'Ordner ändern' : 'Ordner festlegen'}
                            </button>
                            {name && (
                                <button
                                    type="button"
                                    onClick={() => { void handleClearBackupFolder(slot); }}
                                    className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-300"
                                >
                                    Entfernen
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <p className="text-xs text-slate-500 leading-relaxed">
                        Fester Ordner nur in Chrome/Edge. Dateiname ist eindeutig — bitte im Downloads-Ordner suchen.
                    </p>
                )}
            </div>
        );
    };

    useEffect(() => {
        // Der Timer läuft NICHT, wenn diese Instanz nur ein Button ist.
        if (variant === 'button') return; 
        if (!isSuperAdminUser()) return;

        const checkBackupTime = () => {
            // Bypass-Weiche: Mobil / Localhost / Dev -> kein automatischer Trigger
            if (shouldBypassAutoBackup()) return;
            if (!isSuperAdminUser()) return;
            const lastBackupStr = localStorage.getItem('LAST_GLOBAL_BACKUP_TIMESTAMP');
            const now = Date.now();
            const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
            
            const masterBackupDue = !lastBackupStr || (now - parseInt(lastBackupStr, 10) > TWENTY_FOUR_HOURS);
            if (masterBackupDue) {
                setShowGuardianModal(true);
            }

            // 72h-GlobalSettings-Prüfung: nur wenn das 24h-Master-Backup NICHT fällig ist (Priorität beim Master)
            checkGlobalSettingsBackupTime(masterBackupDue);
        };

        const checkGlobalSettingsBackupTime = (masterBackupDue: boolean) => {
            // Bypass-Weiche: Mobil / Localhost / Dev -> kein automatischer Trigger
            if (shouldBypassAutoBackup()) return;
            if (!isSuperAdminUser()) return;
            if (masterBackupDue) return; // 24h-Master-Backup hat Vorrang

            const lastSettingsBackupStr = localStorage.getItem('LAST_GLOBAL_SETTINGS_BACKUP_TIMESTAMP');
            const now = Date.now();
            const SEVENTY_TWO_HOURS = 3 * 24 * 60 * 60 * 1000;

            if (!lastSettingsBackupStr || (now - parseInt(lastSettingsBackupStr, 10) > SEVENTY_TWO_HOURS)) {
                setShowGlobalSettingsGuardianModal(true);
            }
        };

        checkBackupTime();
        
        const interval = setInterval(checkBackupTime, 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, [variant]);

    const handleMasterDownload = async () => {
        setIsDownloadingMaster(true);
        showNotification("Sammle alle Daten aus der Cloud...", "success");

        try {
            const masterBackup: any = {
                metadata: {
                    exportDate: new Date().toISOString(),
                    exportedBy: auth.currentUser?.email || "Unknown Admin",
                    appVersion: "1.4",
                    backupType: "master_mandanten",
                    backupLabel: "Master-Mandanten-Backup",
                },
                system_data: {},
                kalkulation_mandanten: [],
                events: [],
                system_events: []
            };

            try {
                const sysRef = doc(db, 'system_data', 'global_settings');
                const sysDoc = await getDoc(sysRef);
                if (sysDoc.exists()) {
                    masterBackup.system_data = sysDoc.data();
                } else {
                    const altSysRef = doc(db, 'system_data', 'master');
                    const altSysDoc = await getDoc(altSysRef);
                    if (altSysDoc.exists()) masterBackup.system_data = altSysDoc.data();
                }
            } catch(e) {
                console.error("Systemdaten konnten nicht exportiert werden", e);
            }

            const querySnapshot = await getDocs(collection(db, 'kalkulation_mandanten'));
            
            for (const docSnap of querySnapshot.docs) {
                const clientData = docSnap.data();
                const clientId = docSnap.id;
                
                const jahreCollection = await getDocs(collection(db, 'kalkulation_mandanten', clientId, 'jahre'));
                const jahreData: any = {};
                
                jahreCollection.forEach(jahrSnap => {
                    jahreData[jahrSnap.id] = jahrSnap.data();
                });

                masterBackup.kalkulation_mandanten.push({
                    id: clientId,
                    rootData: clientData,
                    jahre: jahreData
                });
            }

            const offlineEventsSnapshot = await getDocs(collection(db, 'events'));
            for (const eventSnap of offlineEventsSnapshot.docs) {
                masterBackup.events.push({
                    id: eventSnap.id,
                    data: eventSnap.data()
                });
            }

            const systemEventsSnapshot = await getDocs(collection(db, 'system_events'));
            for (const eventSnap of systemEventsSnapshot.docs) {
                masterBackup.system_events.push({
                    id: eventSnap.id,
                    data: eventSnap.data()
                });
            }

            const dateStr = new Date().toISOString().split('T')[0];
            const fileName = `PreisExpert_Master-Mandanten-Backup_${dateStr}.json`;
            const where = await saveBackupJson(fileName, masterBackup, 'master');

            localStorage.setItem('LAST_GLOBAL_BACKUP_TIMESTAMP', Date.now().toString());
            setShowGuardianModal(false);
            showNotification(
                where === 'folder'
                    ? `Master-Mandanten-Backup gespeichert in „${getSavedBackupFolderName('master') || 'Ordner'}“.`
                    : 'Master-Mandanten-Backup heruntergeladen!',
                'success'
            );

        } catch (error) {
            console.error("Fehler beim Master-Mandanten Download", error);
            showNotification("Fehler beim Erstellen des Master-Mandanten-Backups", "error");
        } finally {
            setIsDownloadingMaster(false);
        }
    };

    // 72h-System: Exportiert AUSSCHLIESSLICH die globalen Einstellungsdaten (ohne Mandanten/Events)
    const handleGlobalSettingsDownload = async () => {
        setIsDownloadingGlobalSettings(true);
        showNotification("Sammle globale Einstellungsdaten aus der Cloud...", "success");

        try {
            let systemData: any = {};

            const sysRef = doc(db, 'system_data', 'global_settings');
            const sysDoc = await getDoc(sysRef);
            if (sysDoc.exists()) {
                systemData = sysDoc.data();
            } else {
                const altSysRef = doc(db, 'system_data', 'master');
                const altSysDoc = await getDoc(altSysRef);
                if (altSysDoc.exists()) systemData = altSysDoc.data();
            }

            const globalSettingsBackup = {
                metadata: {
                    exportDate: new Date().toISOString(),
                    exportedBy: auth.currentUser?.email || "Unknown Admin",
                    backupType: "global",
                    backupLabel: "Global-Backup",
                },
                settings: systemData.settings ?? null,
                team: systemData.team ?? null,
                teamRegistry: systemData.teamRegistry ?? null,
                appSettings: systemData.appSettings ?? null
            };

            const dateStr = new Date().toISOString().split('T')[0];
            const fileName = `PreisExpert_Global-Backup_${dateStr}.json`;
            const where = await saveBackupJson(fileName, globalSettingsBackup, 'global');

            localStorage.setItem('LAST_GLOBAL_SETTINGS_BACKUP_TIMESTAMP', Date.now().toString());
            setShowGlobalSettingsGuardianModal(false);
            showNotification(
                where === 'folder'
                    ? `Global-Backup gespeichert in „${getSavedBackupFolderName('global') || 'Ordner'}“.`
                    : 'Global-Backup heruntergeladen!',
                'success'
            );

        } catch (error) {
            console.error("Fehler beim Global-Backup Download", error);
            showNotification("Fehler beim Erstellen des Global-Backups", "error");
        } finally {
            setIsDownloadingGlobalSettings(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const jsonString = event.target?.result as string;
                const parsedData = JSON.parse(jsonString);

                if (!Array.isArray(parsedData.kalkulation_mandanten)) {
                    throw new Error("Ungültiges Format. Die Datei enthält keine 'kalkulation_mandanten'.");
                }

                setRestoreData(parsedData);
                setRestoreMode(null);
                setSingleAkteId('');
                setSingleAkteSearch('');
                setRestoreCode('');
                setRestoreError(false);
                setShowRestoreModal(true);
            } catch (err: any) {
                console.error("Fehler beim Parse:", err);
                showNotification(`Upload fehlgeschlagen: ${err.message}`, "error");
            } finally {
                if (fileInputRef.current) fileInputRef.current.value = ''; 
            }
        };

        reader.onerror = () => {
            showNotification("Lokaler Dateilesefehler.", "error");
            if (fileInputRef.current) fileInputRef.current.value = '';
        };

        reader.readAsText(file);
    };

    const writeOneClientToFirebase = async (client: any) => {
        if (!client?.id || !client.rootData) {
            throw new Error('Akte unvollständig (id/rootData fehlen).');
        }
        await setDoc(doc(db, 'kalkulation_mandanten', client.id), client.rootData);
        if (client.jahre && typeof client.jahre === 'object') {
            for (const [jahrKey, jahrData] of Object.entries(client.jahre)) {
                await setDoc(doc(db, 'kalkulation_mandanten', client.id, 'jahre', jahrKey), jahrData as any);
            }
        }
    };

    const executeSingleAkteRestore = async () => {
        if (restoreCode.toUpperCase() !== 'AKTE') {
            setRestoreError(true);
            return;
        }
        if (!restoreData || !singleAkteId) {
            showNotification('Bitte eine Akte auswählen.', 'error');
            return;
        }

        const client = (restoreData.kalkulation_mandanten || []).find(
            (c: any) => String(c.id) === String(singleAkteId)
        );
        if (!client) {
            showNotification('Akte im Backup nicht gefunden.', 'error');
            return;
        }

        setIsRestoring(true);
        setRestoreError(false);
        try {
            showNotification(`Stelle nur Akte ${client.id} wieder her…`, 'success');
            await writeOneClientToFirebase(client);
            const labelInfo = getClientLabel(client);
            const label = labelInfo.personName || labelInfo.name || client.id;
            showNotification(`Akte wiederhergestellt: ${label}. Lade neu…`, 'success');
            setTimeout(() => window.location.reload(), 1500);
        } catch (err: any) {
            console.error('Einzelakte-Restore fehlgeschlagen:', err);
            showNotification(`Restore fehlgeschlagen: ${err.message}`, 'error');
            setIsRestoring(false);
        }
    };

    const executeRestore = async () => {
        // 💉 CHIRURGIE: Groß-/Kleinschreibung ignorieren, um die Eingabe bequemer zu machen
        if (restoreCode.toUpperCase() !== 'WIEDERHERSTELLEN') {
            setRestoreError(true);
            return;
        }
        if (!restoreData) return;

        setIsRestoring(true);
        setRestoreError(false);
        
        try {
            showNotification("Initiiere globale System-Überschreibung...", "success");

            if (restoreData.system_data) {
                await setDoc(doc(db, 'system_data', 'global_settings'), restoreData.system_data);
                console.log("💉 System-Daten geschrieben.");
            }

            const clients = restoreData.kalkulation_mandanten || [];
            for (const client of clients) {
                if (!client.id || !client.rootData) continue;
                await writeOneClientToFirebase(client);
            }

            let restoredOfflineEventsCount = 0;
            if (Array.isArray(restoreData.events)) {
                for (const event of restoreData.events) {
                    if (!event || typeof event !== 'object' || !event.id) {
                        console.warn("💉 Offline-Event übersprungen: fehlende oder ungültige ID.", event);
                        continue;
                    }

                    let eventPayload: any;
                    if (event.data && typeof event.data === 'object') {
                        eventPayload = event.data;
                    } else {
                        const { id, ...rest } = event;
                        if (Object.keys(rest).length === 0) {
                            console.warn(`💉 Offline-Event übersprungen (${event.id}): keine Daten gefunden.`, event);
                            continue;
                        }
                        eventPayload = rest;
                    }

                    await setDoc(doc(db, 'events', event.id), eventPayload);
                    restoredOfflineEventsCount++;
                }
                console.log(`💉 ${restoredOfflineEventsCount} Offline-Events (Abrechnungstool) erfolgreich wiederhergestellt.`);
            } else if (restoreData.events != null) {
                console.warn("💉 restoreData.events ist kein Array – Offline-Events werden übersprungen.", restoreData.events);
            }

            let restoredSystemEventsCount = 0;
            if (Array.isArray(restoreData.system_events)) {
                for (const event of restoreData.system_events) {
                    if (!event || typeof event !== 'object' || !event.id) {
                        console.warn("💉 Seminar-Event übersprungen: fehlende oder ungültige ID.", event);
                        continue;
                    }

                    let eventPayload: any;
                    if (event.data && typeof event.data === 'object') {
                        eventPayload = event.data;
                    } else {
                        const { id, ...rest } = event;
                        if (Object.keys(rest).length === 0) {
                            console.warn(`💉 Seminar-Event übersprungen (${event.id}): keine Daten gefunden.`, event);
                            continue;
                        }
                        eventPayload = rest;
                    }

                    await setDoc(doc(db, 'system_events', event.id), eventPayload);
                    restoredSystemEventsCount++;
                }
                console.log(`💉 ${restoredSystemEventsCount} Online-Seminare (Seminar-Manager) erfolgreich wiederhergestellt.`);
            } else if (restoreData.system_events != null) {
                console.warn("💉 restoreData.system_events ist kein Array – Online-Seminare werden übersprungen.", restoreData.system_events);
            }

            console.log(`💉 System-Restore erfolgreich. ${clients.length} Akten überschrieben.`);
            showNotification("Master-Restore ERFOLGREICH! System wird neu geladen...", "success");
            
            setTimeout(() => {
                window.location.reload();
            }, 2000);

        } catch (err: any) {
            console.error("Fehler beim System Restore:", err);
            showNotification(`Restore fehlgeschlagen: ${err.message}`, "error");
            setIsRestoring(false);
        }
    };

    const closeRestoreModal = () => {
        if (isRestoring) return;
        setShowRestoreModal(false);
        setRestoreData(null);
        setRestoreCode('');
        setRestoreError(false);
        setRestoreMode(null);
        setSingleAkteId('');
        setSingleAkteSearch('');
    };

    const getClientLabel = (c: any) => {
        const root = c?.rootData || {};
        const cd = root.clientData || {};
        const vorname = root.vorname || cd.vorname || '';
        const nachname = root.nachname || cd.nachname || '';
        // Wie im CRM: Person zuerst, Salon nur als Untertitel
        const personName = `${vorname} ${nachname}`.trim();
        const salon =
            root.firmenname ||
            cd.firmenname ||
            root.salonname ||
            cd.salonname ||
            '';
        const name = personName || salon || 'Ohne Name';
        const nr = root.mandantNr || cd.mandantNr || '—';
        return { name, personName, salon, vorname, nachname, nr, id: c.id };
    };

    const filteredClients = (() => {
        const list = Array.isArray(restoreData?.kalkulation_mandanten) ? restoreData.kalkulation_mandanten : [];
        const q = singleAkteSearch.trim().toLowerCase();
        const matched = !q
            ? [...list]
            : list.filter((c: any) => {
                const { personName, salon, nr, id, vorname, nachname } = getClientLabel(c);
                const hay = `${personName} ${vorname} ${nachname} ${salon} ${nr} ${id}`.toLowerCase();
                return hay.includes(q);
            });
        return matched
            .sort((a: any, b: any) => {
                const nameA = getClientLabel(a).personName || getClientLabel(a).name;
                const nameB = getClientLabel(b).personName || getClientLabel(b).name;
                return nameA.localeCompare(nameB, 'de');
            })
            .slice(0, 40);
    })();

    const guardianModal = (
        <AnimatePresence>
            {showGuardianModal && isSuperAdminUser() && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100000] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
                    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-slate-900 border border-[#E6C299]/40 rounded-2xl p-8 max-w-md w-full shadow-[0_0_50px_rgba(230,194,153,0.15)] flex flex-col items-center text-center font-sans">
                        <div className="w-16 h-16 bg-[#E6C299]/10 rounded-full flex items-center justify-center mb-6 shadow-inner border border-[#E6C299]/20">
                            <ShieldAlertIcon size={32} className="text-[#E6C299]" />
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#E6C299]/70 mb-2">24h · Alle Mandanten + System</div>
                        <h2 className="text-[#E6C299] text-2xl font-black uppercase tracking-widest mb-4">Master-Mandanten-Backup</h2>
                        <p className="bg-slate-800 border border-slate-700 text-slate-300 mb-4 text-sm leading-relaxed p-4 rounded-xl">
                            Dein letztes <strong className="text-[#E6C299]">Master-Mandanten-Backup</strong> (komplette DB inkl. aller Akten) ist älter als 24 Stunden. Bitte jetzt sichern.
                        </p>
                        <FolderHint slot="master" accent="gold" />
                        <button
                            onClick={handleMasterDownload}
                            disabled={isDownloadingMaster}
                            className={`w-full ${isDownloadingMaster ? 'bg-[#dcb07b] text-slate-700' : 'bg-[#E6C299] hover:bg-[#dcb07b] text-slate-900'} font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-colors shadow-lg`}
                        >
                            {isDownloadingMaster ? 'Lade Daten...' : 'Master-Mandanten-Backup speichern'}
                        </button>
                        <button
                            type="button"
                            disabled={isDownloadingMaster}
                            onClick={() => {
                                // Nur dieses Gerät: Reminder für 24h zurücksetzen (Backup läuft woanders)
                                localStorage.setItem('LAST_GLOBAL_BACKUP_TIMESTAMP', Date.now().toString());
                                setShowGuardianModal(false);
                            }}
                            className="w-full mt-3 bg-transparent hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700 font-black uppercase tracking-widest text-sm py-3 rounded-xl transition-colors disabled:opacity-50"
                        >
                            Abbrechen
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    // 72h: nur globale Einstellungen — bewusst anderes Wording + Farbe gegen Verwechslung
    const globalSettingsGuardianModal = (
        <AnimatePresence>
            {showGlobalSettingsGuardianModal && isSuperAdminUser() && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100000] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
                    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-slate-900 border border-cyan-500/40 rounded-2xl p-8 max-w-md w-full shadow-[0_0_50px_rgba(34,211,238,0.12)] flex flex-col items-center text-center font-sans">
                        <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mb-6 shadow-inner border border-cyan-500/20">
                            <ShieldAlertIcon size={32} className="text-cyan-400" />
                        </div>
                        <div className="text-[10px] font-black uppercase tracking-[0.25em] text-cyan-400/80 mb-2">72h · Nur Settings / Team / Registry</div>
                        <h2 className="text-cyan-300 text-2xl font-black uppercase tracking-widest mb-4">Global-Backup</h2>
                        <p className="bg-slate-800 border border-slate-700 text-slate-300 mb-4 text-sm leading-relaxed p-4 rounded-xl">
                            Dein letztes <strong className="text-cyan-300">Global-Backup</strong> (nur Einstellungen, Team, Registry — <em>ohne</em> Mandanten-Akten) ist älter als 3 Tage. Bitte jetzt sichern.
                        </p>
                        <FolderHint slot="global" accent="cyan" />
                        <button
                            onClick={handleGlobalSettingsDownload}
                            disabled={isDownloadingGlobalSettings}
                            className={`w-full ${isDownloadingGlobalSettings ? 'bg-cyan-800 text-cyan-200' : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950'} font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-colors shadow-lg`}
                        >
                            {isDownloadingGlobalSettings ? 'Lade Daten...' : 'Global-Backup speichern'}
                        </button>
                        <button
                            type="button"
                            disabled={isDownloadingGlobalSettings}
                            onClick={() => {
                                // Nur dieses Gerät: Reminder für 72h zurücksetzen (Backup läuft woanders)
                                localStorage.setItem('LAST_GLOBAL_SETTINGS_BACKUP_TIMESTAMP', Date.now().toString());
                                setShowGlobalSettingsGuardianModal(false);
                            }}
                            className="w-full mt-3 bg-transparent hover:bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700 font-black uppercase tracking-widest text-sm py-3 rounded-xl transition-colors disabled:opacity-50"
                        >
                            Abbrechen
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    const restoreModal = (
        <AnimatePresence>
            {showRestoreModal && restoreData && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100001] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.95 }}
                        className={`bg-[#15171e] border ${restoreMode === 'single' ? 'border-amber-500/40' : 'border-red-500/50'} rounded-2xl p-8 ${restoreMode === 'single' ? 'max-w-lg' : 'max-w-md'} w-full shadow-[0_0_50px_rgba(220,38,38,0.3)] flex flex-col items-center text-center font-sans relative max-h-[90vh] overflow-y-auto`}
                    >
                        <button onClick={closeRestoreModal} disabled={isRestoring} className="absolute top-4 right-4 text-slate-500 hover:text-white">
                            ✕
                        </button>

                        {/* Schritt 1: Modus wählen */}
                        {restoreMode === null && (
                            <>
                                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-600">
                                    <ShieldAlertIcon size={32} className="text-[#E6C299]" />
                                </div>
                                <h2 className="text-[#E6C299] text-xl font-black uppercase tracking-widest mb-2">Backup erkannt</h2>
                                <p className="text-slate-400 text-sm mb-2">
                                    Backup vom {new Date(restoreData?.metadata?.exportDate || Date.now()).toLocaleDateString('de-DE')} · {restoreData?.kalkulation_mandanten?.length || 0} Akten
                                </p>
                                <p className="text-slate-500 text-xs mb-6">Was möchtest du wiederherstellen?</p>
                                <div className="w-full space-y-3">
                                    <button
                                        type="button"
                                        onClick={() => { setRestoreMode('single'); setRestoreCode(''); setRestoreError(false); }}
                                        className="w-full bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/40 text-amber-200 font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-colors"
                                    >
                                        Nur eine Akte
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setRestoreMode('full'); setRestoreCode(''); setRestoreError(false); }}
                                        className="w-full bg-red-900/20 hover:bg-red-900/40 border border-red-500/40 text-red-300 font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-colors"
                                    >
                                        Gesamtes System
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Schritt 2a: Einzelakte */}
                        {restoreMode === 'single' && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => { setRestoreMode(null); setRestoreCode(''); setRestoreError(false); }}
                                    disabled={isRestoring}
                                    className="absolute top-4 left-4 text-[10px] uppercase font-bold text-slate-500 hover:text-white"
                                >
                                    ← Zurück
                                </button>
                                <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-6 border border-amber-500/20">
                                    <ShieldAlertIcon size={32} className="text-amber-400" />
                                </div>
                                <h2 className="text-amber-400 text-xl font-black uppercase tracking-widest mb-2">Einzelakte wiederherstellen</h2>
                                <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                                    Nur die gewählte Akte wird überschrieben. Alle anderen Mandanten bleiben unverändert.
                                </p>
                                <input
                                    type="text"
                                    value={singleAkteSearch}
                                    onChange={(e) => setSingleAkteSearch(e.target.value)}
                                    placeholder="Vor-/Nachname oder Mandant-Nr…"
                                    disabled={isRestoring}
                                    className="w-full bg-black border border-slate-700 text-slate-200 rounded-lg px-4 py-2.5 text-sm mb-3 outline-none focus:border-amber-500"
                                    autoFocus
                                />
                                <div className="w-full max-h-48 overflow-y-auto border border-slate-800 rounded-lg mb-4 text-left">
                                    {filteredClients.length === 0 ? (
                                        <p className="text-slate-500 text-xs p-4 text-center">Keine Akte gefunden.</p>
                                    ) : (
                                        filteredClients.map((c: any) => {
                                            const { name, salon, nr, id } = getClientLabel(c);
                                            const selected = String(singleAkteId) === String(id);
                                            return (
                                                <button
                                                    key={id}
                                                    type="button"
                                                    disabled={isRestoring}
                                                    onClick={() => setSingleAkteId(String(id))}
                                                    className={`w-full px-3 py-2.5 border-b border-slate-800/80 text-left transition-colors ${selected ? 'bg-amber-500/20 border-l-2 border-l-amber-400' : 'hover:bg-slate-800/50'}`}
                                                >
                                                    <div className="text-sm font-bold text-white truncate leading-tight">{name}</div>
                                                    {salon && salon !== name && (
                                                        <div className="text-[11px] text-[#E6C299] font-bold uppercase tracking-wide truncate mt-0.5">{salon}</div>
                                                    )}
                                                    <div className="text-[10px] text-slate-500 font-mono truncate mt-0.5">{nr} · {id}</div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                                {singleAkteId && (
                                    <p className="text-[10px] text-amber-200/80 font-mono mb-3 break-all">Gewählt: {singleAkteId}</p>
                                )}
                                <div className="w-full space-y-3">
                                    <input
                                        type="text"
                                        value={restoreCode}
                                        onChange={(e) => { setRestoreCode(e.target.value); setRestoreError(false); }}
                                        placeholder="Tippe 'AKTE'"
                                        className="w-full bg-black border border-amber-900/50 text-amber-300 rounded-lg px-4 py-3 text-center tracking-widest font-mono focus:border-amber-500 outline-none uppercase"
                                        disabled={isRestoring || !singleAkteId}
                                    />
                                    {restoreError && <p className="text-red-500 text-xs text-center font-bold animate-pulse">FALSCHER SICHERHEITS-CODE.</p>}
                                    <button
                                        type="button"
                                        onClick={executeSingleAkteRestore}
                                        disabled={isRestoring || !singleAkteId}
                                        className={`w-full ${isRestoring ? 'bg-amber-900' : 'bg-amber-600 hover:bg-amber-500'} disabled:opacity-40 text-black font-black tracking-widest uppercase py-4 rounded-lg transition-colors`}
                                    >
                                        {isRestoring ? 'Akte wird geschrieben…' : 'Diese Akte wiederherstellen'}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Schritt 2b: Voll-Restore */}
                        {restoreMode === 'full' && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => { setRestoreMode(null); setRestoreCode(''); setRestoreError(false); }}
                                    disabled={isRestoring}
                                    className="absolute top-4 left-4 text-[10px] uppercase font-bold text-slate-500 hover:text-white"
                                >
                                    ← Zurück
                                </button>
                                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-6 shadow-inner border border-red-500/20">
                                    <ShieldAlertIcon size={32} className="text-red-500" />
                                </div>
                                <h2 className="text-red-500 text-2xl font-black uppercase tracking-widest mb-2">Globale Überschreibung</h2>
                                <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                                    Achtung! Du bist dabei, die <strong>gesamte Datenbank</strong> durch das hochgeladene Backup vom {new Date(restoreData?.metadata?.exportDate || Date.now()).toLocaleDateString('de-DE')} zu ersetzen.
                                </p>
                                <div className="bg-red-950/30 border border-red-900/50 w-full p-4 rounded-lg mb-6 text-left">
                                    <ul className="text-xs text-red-200/80 font-mono space-y-2 font-bold">
                                        <li>✓ System-Settings erkannt.</li>
                                        <li>✓ {restoreData?.kalkulation_mandanten?.length || 0} Mandanten-Akten erkannt.</li>
                                        <li>✓ {restoreData?.events?.length || 0} Offline-Events (Abrechnungstool) erkannt.</li>
                                        <li>✓ {restoreData?.system_events?.length || 0} Online-Seminare (Seminar-Manager) erkannt.</li>
                                        <li>⚠️ Alle abweichenden Online-Daten werden überschrieben!</li>
                                    </ul>
                                </div>
                                <div className="w-full space-y-4">
                                    <input
                                        type="text"
                                        value={restoreCode}
                                        onChange={(e) => { setRestoreCode(e.target.value); setRestoreError(false); }}
                                        placeholder="Tippe 'WIEDERHERSTELLEN'"
                                        className="w-full bg-black border border-red-900 text-red-400 rounded-lg px-4 py-3 text-center tracking-widest font-mono focus:border-red-500 outline-none uppercase"
                                        disabled={isRestoring}
                                        autoFocus
                                    />
                                    {restoreError && <p className="text-red-500 text-xs text-center font-bold animate-pulse">FALSCHER SICHERHEITS-CODE.</p>}
                                    <button
                                        type="button"
                                        onClick={executeRestore}
                                        disabled={isRestoring}
                                        className={`w-full ${isRestoring ? 'bg-red-900' : 'bg-red-600 hover:bg-red-500'} text-white font-black tracking-widest uppercase py-4 rounded-lg transition-colors shadow-lg`}
                                    >
                                        {isRestoring ? 'Datenbank wird geflutet...' : 'Endgültig Überschreiben'}
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <>
            {(variant === 'both' || variant === 'button') && (
                <div className="flex flex-col gap-2 w-full">
                    <button 
                        onClick={handleMasterDownload}
                        disabled={isDownloadingMaster || isRestoring}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-3 ${isDownloadingMaster ? 'text-blue-500' : 'text-slate-400 hover:text-blue-400'} bg-slate-800/30 hover:bg-slate-800 rounded-lg text-[10px] uppercase font-bold transition-colors border border-transparent hover:border-blue-500/30`}
                        title="Master-Mandanten-Backup: alle Mandanten + System als JSON sichern."
                    >
                        <DownloadIcon size={14} className={isDownloadingMaster ? "animate-bounce" : ""} /> 
                        {isDownloadingMaster ? 'LÄDT DATEN...' : 'MASTER-MANDANTEN-BACKUP'}
                    </button>

                    <button
                        onClick={handleGlobalSettingsDownload}
                        disabled={isDownloadingGlobalSettings || isRestoring}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-3 ${isDownloadingGlobalSettings ? 'text-cyan-400' : 'text-slate-400 hover:text-cyan-300'} bg-slate-800/30 hover:bg-slate-800 rounded-lg text-[10px] uppercase font-bold transition-colors border border-transparent hover:border-cyan-500/30`}
                        title="Global-Backup: nur Settings, Team und Registry (ohne Mandanten-Akten)."
                    >
                        <DownloadIcon size={14} className={isDownloadingGlobalSettings ? "animate-bounce" : ""} />
                        {isDownloadingGlobalSettings ? 'LÄDT DATEN...' : 'GLOBAL-BACKUP'}
                    </button>

                    {canPickFolder && (
                        <div className="flex flex-col gap-1.5">
                            <button
                                type="button"
                                onClick={() => { void handlePickBackupFolder('master'); }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-500 hover:text-[#E6C299] bg-transparent hover:bg-slate-800/40 rounded-lg text-[9px] uppercase font-bold transition-colors border border-dashed border-slate-700 hover:border-[#E6C299]/40"
                                title="Festen Ordner nur für Master-Mandanten-Backups (Chrome/Edge)."
                            >
                                {masterFolderName ? `Master-Ordner: ${masterFolderName}` : 'Master-Ordner festlegen'}
                            </button>
                            <button
                                type="button"
                                onClick={() => { void handlePickBackupFolder('global'); }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-500 hover:text-cyan-300 bg-transparent hover:bg-slate-800/40 rounded-lg text-[9px] uppercase font-bold transition-colors border border-dashed border-slate-700 hover:border-cyan-500/40"
                                title="Festen Ordner nur für Global-Backups (Chrome/Edge)."
                            >
                                {globalFolderName ? `Global-Ordner: ${globalFolderName}` : 'Global-Ordner festlegen'}
                            </button>
                        </div>
                    )}

                    <input type="file" accept=".json" ref={fileInputRef} onChange={handleFileSelect} className="hidden" />
                    <button 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isDownloadingMaster || isDownloadingGlobalSettings || isRestoring}
                        className="w-full flex items-center justify-center gap-2 px-3 py-3 text-red-400 hover:text-red-300 bg-red-900/10 hover:bg-red-900/30 rounded-lg text-[10px] uppercase font-bold transition-colors border border-red-900/20 hover:border-red-500/50"
                        title="Master-Mandanten-Backup hochladen: gesamte DB oder nur eine einzelne Akte wiederherstellen."
                    >
                        <UploadIcon size={14} className={isRestoring ? "animate-bounce" : ""} /> 
                        {isRestoring ? 'INJIZIERE DATEN...' : 'SYSTEM-RESTORE'}
                    </button>
                </div>
            )}

            {typeof document !== 'undefined' && createPortal(
                <>
                    {guardianModal}
                    {globalSettingsGuardianModal}
                    {restoreModal}
                </>, 
                document.body
            )}
        </>
    );
};

export default GlobalBackupManager;